import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAppDispatch } from "./store/hooks";
import { fetchMe } from "./store/slices/authSlice";
import { useAuth } from "./hooks/useAuth";
import { Layout } from "./components/layout/Layout";
import { PageLoader } from "./components/ui/Button";

// Public
import Login from "./pages/public/Login";

// User
import Dashboard from "./pages/user/Dashboard";
import Trade from "./pages/user/Trade";
import Deposit from "./pages/user/Deposit";
import Withdrawal from "./pages/user/Withdrawal";
import Transactions from "./pages/user/Transactions";
import KYC from "./pages/user/KYC";
import Profile from "./pages/user/Profile";

// Admin
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/Users";
import { AdminDeposits, AdminWithdrawals } from "./pages/admin/Deposits";
import KYCApprovals from "./pages/admin/KYCApprovals";
import CryptoAddresses from "./pages/admin/CryptoAddresses";
import Settings from "./pages/admin/Settings";
import AdminTrades from "./pages/admin/AdminTrades";
import Register from "./pages/public/Register";
import ForgotPassword from "./pages/public/ForgotPassword";

// Guards
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token } = useAuth();

  useEffect(() => {
    if (token) dispatch(fetchMe());
  }, [token, dispatch]);

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* User */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <Layout>
              <Dashboard />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/trade"
        element={
          <RequireAuth>
            <Layout>
              <Trade />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/deposit"
        element={
          <RequireAuth>
            <Layout>
              <Deposit />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/withdraw"
        element={
          <RequireAuth>
            <Layout>
              <Withdrawal />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/transactions"
        element={
          <RequireAuth>
            <Layout>
              <Transactions />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/kyc"
        element={
          <RequireAuth>
            <Layout>
              <KYC />
            </Layout>
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <Layout>
              <Profile />
            </Layout>
          </RequireAuth>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <Layout>
              <AdminDashboard />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RequireAdmin>
            <Layout>
              <AdminUsers />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/deposits"
        element={
          <RequireAdmin>
            <Layout>
              <AdminDeposits />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/withdrawals"
        element={
          <RequireAdmin>
            <Layout>
              <AdminWithdrawals />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/kyc"
        element={
          <RequireAdmin>
            <Layout>
              <KYCApprovals />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/addresses"
        element={
          <RequireAdmin>
            <Layout>
              <CryptoAddresses />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <RequireAdmin>
            <Layout>
              <Settings />
            </Layout>
          </RequireAdmin>
        }
      />
      <Route
        path="/admin/trades"
        element={
          <RequireAdmin>
            <Layout>
              <AdminTrades />
            </Layout>
          </RequireAdmin>
        }
      />

      {/* Catch all */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AppRoutes;
